import React, { useState } from 'react';
import {
  LockClosedIcon,
  FingerPrintIcon,
  GlobeAltIcon,
  EyeSlashIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ArrowPathIcon,
  BellIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export const PlatformSecurity: React.FC = () => {
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedLogs, setSelectedLogs] = useState<string[]>([]);
  const navigate = useNavigate();
  
  // Mock data
  const trustedIPs = [
    { id: 1, ip: '192.168.1.100', location: 'New York Office', lastAccess: '2024-03-20 14:30:00' },
    { id: 2, ip: '10.0.0.50', location: 'London Office', lastAccess: '2024-03-20 10:15:00' }
  ];

  const blockedIPs = [
    { id: 1, ip: '203.0.113.0', location: 'Unknown', attempts: 5, lastAttempt: '2024-03-20 08:45:00' },
    { id: 2, ip: '198.51.100.0', location: 'Unknown', attempts: 3, lastAttempt: '2024-03-20 07:30:00' }
  ];

  const auditLogs = [
    { id: 1, event: 'User Login', details: 'Admin user login from trusted IP', timestamp: '2024-03-20 14:30:00', severity: 'info' },
    { id: 2, event: 'Failed 2FA', details: 'Multiple failed 2FA attempts', timestamp: '2024-03-20 12:15:00', severity: 'warning' }
  ];

  const backupHistory = [
    { id: 1, type: 'Full Backup', status: 'Completed', size: '2.5GB', timestamp: '2024-03-20 00:00:00' },
    { id: 2, type: 'Incremental', status: 'In Progress', size: '500MB', timestamp: '2024-03-20 12:00:00' }
  ];

  const handleExportLogs = (type: 'audit' | 'system') => {
    const timestamp = new Date().toISOString().split('T')[0];
    const fileName = `${type}-logs-${timestamp}.${exportFormat}`;
    
    // Mock export functionality
    const element = document.createElement('a');
    let content = '';

    if (type === 'audit') {
      content = 'Event,Details,Timestamp,Severity\n';
      auditLogs.forEach(log => {
        content += `${log.event},${log.details},${log.timestamp},${log.severity}\n`;
      });
    } else {
      content = 'Type,Status,Size,Timestamp\n';
      backupHistory.forEach(backup => {
        content += `${backup.type},${backup.status},${backup.size},${backup.timestamp}\n`;
      });
    }

    const blob = new Blob([content], { type: 'text/csv' });
    element.href = URL.createObjectURL(blob);
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleEncryptionConfig = () => {
    navigate('/superadmin/security/encryption-config');
  };

  const handle2FAConfig = () => {
    navigate('/superadmin/security/configure-2fa');
  };

  const handleAddIPAddress = () => {
    navigate('/superadmin/security/add-ip');
  };

  const handleConfigureDataMasking = () => {
    navigate('/superadmin/security/data-masking');
  };

  const handleViewLiveLogs = () => {
    navigate('/superadmin/security/live-logs');
  };

  const handleConfigureBackup = () => {
    navigate('/superadmin/security/configure-backup');
  };

  return (
    <div className="bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Platform Security</h1>
          <p className="mt-2 text-sm text-gray-600">Manage and monitor platform security settings and activities</p>
        </div>

        {/* Section 1: End to End Encryption */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <LockClosedIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">End to End Encryption</h2>
            </div>
            <div className="flex items-center">
              <span className="px-3 py-1 text-sm text-green-800 bg-green-100 rounded-full">Active</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Message Encryption</h3>
                <p className="text-sm text-gray-600">AES-256 encryption for all messages</p>
              </div>
              <button 
                onClick={handleEncryptionConfig}
                className="text-[#FF5722] hover:text-[#F4511E]"
              >
                Configure
              </button>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Storage Encryption</h3>
                <p className="text-sm text-gray-600">Database and file storage encryption</p>
              </div>
              <button 
                onClick={handleEncryptionConfig}
                className="text-[#FF5722] hover:text-[#F4511E]"
              >
                Configure
              </button>
            </div>
          </div>
        </div>

        {/* Section 2: Two Factor Authentication */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FingerPrintIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Two Factor Authentication (2FA)</h2>
            </div>
            <button 
              onClick={handle2FAConfig}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
            >
              Configure 2FA
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Active Users with 2FA</h3>
                <p className="text-2xl font-bold text-[#FF5722]">85%</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Failed Attempts (24h)</h3>
                <p className="text-2xl font-bold text-[#FF5722]">12</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Recovery Codes Used</h3>
                <p className="text-2xl font-bold text-[#FF5722]">3</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: IP Whitelisting */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <GlobeAltIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">IP Whitelisting</h2>
            </div>
            <button 
              onClick={handleAddIPAddress}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
            >
              Add IP Address
            </button>
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Trusted IP Addresses</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Access</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trustedIPs.map((ip) => (
                      <tr key={ip.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.ip}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.location}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.lastAccess}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button className="text-red-600 hover:text-red-800">
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Blocked IP Addresses</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed Attempts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Attempt</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {blockedIPs.map((ip) => (
                      <tr key={ip.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.ip}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.attempts}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ip.lastAttempt}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button className="text-[#FF5722] hover:text-[#F4511E]">Unblock</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Data Masking */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <EyeSlashIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Data Masking</h2>
            </div>
            <button 
              onClick={handleConfigureDataMasking}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
            >
              Configure Rules
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Phone Number Masking</h3>
                <p className="text-sm text-gray-600 mt-1">Format: +XX-XXX-XXX-XX89</p>
                <div className="mt-2">
                  <label className="inline-flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">Enable masking</span>
                  </label>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Email Address Masking</h3>
                <p className="text-sm text-gray-600 mt-1">Format: j***@example.com</p>
                <div className="mt-2">
                  <label className="inline-flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">Enable masking</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Security Audit */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Security Audit</h2>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <select
                  className="border rounded-md p-2 text-sm"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <option value="csv">CSV</option>
                  <option value="xlsx">Excel</option>
                  <option value="pdf">PDF</option>
                </select>
                <button 
                  onClick={() => handleExportLogs('audit')}
                  className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="border rounded-md p-2"
                />
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="border rounded-md p-2"
                />
                <button className="flex items-center space-x-2 bg-[#FF5722] text-white px-3 py-2 rounded-md hover:bg-[#F4511E]">
                  <FunnelIcon className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input 
                      type="checkbox" 
                      className="form-checkbox text-[#FF5722] rounded"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLogs(auditLogs.map(log => log.id.toString()));
                        } else {
                          setSelectedLogs([]);
                        }
                      }}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input 
                        type="checkbox" 
                        className="form-checkbox text-[#FF5722] rounded"
                        checked={selectedLogs.includes(log.id.toString())}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLogs([...selectedLogs, log.id.toString()]);
                          } else {
                            setSelectedLogs(selectedLogs.filter(id => id !== log.id.toString()));
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.event}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.details}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.timestamp}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        log.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {log.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 6: Logging and Monitoring */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ChartBarIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Logging and Monitoring</h2>
            </div>
            <div className="flex items-center space-x-4">
              <select
                className="border rounded-md p-2 text-sm"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <option value="csv">CSV</option>
                <option value="xlsx">Excel</option>
                <option value="pdf">PDF</option>
              </select>
              <button 
                onClick={() => handleExportLogs('system')}
                className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                <span>Export System Logs</span>
              </button>
              <button 
                onClick={handleViewLiveLogs}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
              >
                View Live Logs
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900">Active Sessions</h3>
              <p className="text-2xl font-bold text-[#FF5722]">124</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900">Security Alerts (24h)</h3>
              <p className="text-2xl font-bold text-[#FF5722]">7</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900">System Health</h3>
              <p className="text-2xl font-bold text-green-600">98%</p>
            </div>
          </div>
        </div>

        {/* Section 7: Automated Backup and Recovery */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ArrowPathIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Automated Backup and Recovery</h2>
            </div>
            <button 
              onClick={handleConfigureBackup}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
            >
              Configure Backup
            </button>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Last Backup</h3>
                <p className="text-sm text-gray-600 mt-1">2024-03-20 00:00:00</p>
                <p className="text-sm text-green-600 mt-1">Successful</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Next Scheduled</h3>
                <p className="text-sm text-gray-600 mt-1">2024-03-21 00:00:00</p>
                <p className="text-sm text-blue-600 mt-1">Scheduled</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Storage Used</h3>
                <p className="text-sm text-gray-600 mt-1">45.5 GB / 100 GB</p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                  <div className="bg-[#FF5722] h-2.5 rounded-full" style={{ width: '45.5%' }}></div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Backup Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backupHistory.map((backup) => (
                    <tr key={backup.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{backup.type}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          backup.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {backup.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{backup.size}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{backup.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Section 8: Notifications */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <BellIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
            </div>
            <button className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]">
              Save Preferences
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Security Alerts</h3>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">Failed login attempts</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">Suspicious IP activity</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">2FA changes</span>
                  </label>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">System Notifications</h3>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">Backup status</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">System updates</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="form-checkbox text-[#FF5722]" checked />
                    <span className="ml-2 text-sm text-gray-700">Performance alerts</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformSecurity; 